import { useState, type FormEvent } from 'react';
import { supabase } from '@/lib/supabase';
import { useBranch } from '@/context/BranchContext';
import Modal from '@/components/base/Modal';
import type { Branch } from '@/lib/types';

interface FormState {
  name: string;
  code: string;
  address: string;
  phone: string;
  whatsapp: string;
  logo_url: string;
  instagram_url: string;
  facebook_url: string;
  tiktok_url: string;
  active: boolean;
}

const emptyForm: FormState = {
  name: '',
  code: '',
  address: '',
  phone: '',
  whatsapp: '',
  logo_url: '',
  instagram_url: '',
  facebook_url: '',
  tiktok_url: '',
  active: true,
};

export default function BranchesPage() {
  const { branches, loading, reload } = useBranch();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Branch | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setError(null);
    setModalOpen(true);
  };

  const openEdit = (b: Branch) => {
    setEditing(b);
    setForm({
      name: b.name,
      code: b.code ?? '',
      address: b.address ?? '',
      phone: b.phone ?? '',
      whatsapp: b.whatsapp ?? '',
      logo_url: b.logo_url ?? '',
      instagram_url: b.instagram_url ?? '',
      facebook_url: b.facebook_url ?? '',
      tiktok_url: b.tiktok_url ?? '',
      active: b.active,
    });
    setError(null);
    setModalOpen(true);
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('El nombre es obligatorio.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: form.name.trim(),
        code: form.code.trim() || null,
        address: form.address.trim() || null,
        phone: form.phone.trim() || null,
        whatsapp: form.whatsapp.trim() || null,
        logo_url: form.logo_url.trim() || null,
        instagram_url: form.instagram_url.trim() || null,
        facebook_url: form.facebook_url.trim() || null,
        tiktok_url: form.tiktok_url.trim() || null,
        active: form.active,
      };
      if (editing) {
        const { error: e } = await supabase
          .from('branches')
          .update({ ...payload, updated_at: new Date().toISOString() })
          .eq('id', editing.id);
        if (e) throw e;
      } else {
        const { error: e } = await supabase.from('branches').insert(payload);
        if (e) throw e;
      }
      setModalOpen(false);
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar la sucursal');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (b: Branch) => {
    const { error: e } = await supabase
      .from('branches')
      .update({ active: !b.active, updated_at: new Date().toISOString() })
      .eq('id', b.id);
    if (e) return;
    reload();
  };

  const inputClass =
    'w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400';
  const labelClass = 'block text-sm font-medium text-foreground-800 mb-1.5';

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">Sucursales</h1>
          <p className="mt-1 text-foreground-600">
            Cada sucursal tiene su propio inventario y ventas.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer"
        >
          <i className="ri-add-line" /> Crear sucursal
        </button>
      </div>

      {loading ? (
        <div className="mt-10 flex items-center justify-center py-16">
          <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
        </div>
      ) : branches.length === 0 ? (
        <div className="mt-10 rounded-lg border border-dashed border-background-300 p-12 text-center">
          <i className="ri-building-2-line text-4xl text-foreground-400" />
          <p className="mt-3 text-foreground-600">
            Aún no hay sucursales. Crea tu primera sucursal para comenzar.
          </p>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {branches.map((b) => (
            <div key={b.id} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  {b.logo_url ? (
                    <img src={b.logo_url} alt={b.name} className="w-11 h-11 rounded-lg object-cover shrink-0" />
                  ) : (
                    <div className="w-11 h-11 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center shrink-0">
                      <i className="ri-building-2-line text-xl" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="font-heading font-semibold text-foreground-950 truncate">{b.name}</p>
                    <p className="text-xs text-foreground-500">{b.code || 'Sin código'}</p>
                  </div>
                </div>
                <span
                  className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${
                    b.active ? 'bg-accent-100 text-accent-900' : 'bg-secondary-100 text-secondary-900'
                  }`}
                >
                  {b.active ? 'Activa' : 'Inactiva'}
                </span>
              </div>

              <div className="mt-4 space-y-1.5 text-sm text-foreground-600">
                {b.address && (
                  <p className="flex items-center gap-2">
                    <i className="ri-map-pin-line text-foreground-400" /> {b.address}
                  </p>
                )}
                {b.phone && (
                  <p className="flex items-center gap-2">
                    <i className="ri-phone-line text-foreground-400" /> {b.phone}
                  </p>
                )}
                {b.whatsapp && (
                  <p className="flex items-center gap-2">
                    <i className="ri-whatsapp-line text-foreground-400" /> {b.whatsapp}
                  </p>
                )}
                {(b.instagram_url || b.facebook_url || b.tiktok_url) && (
                  <div className="flex items-center gap-3 pt-1 text-base">
                    {b.instagram_url && (
                      <i className="ri-instagram-line text-accent-600" title="Instagram" />
                    )}
                    {b.facebook_url && (
                      <i className="ri-facebook-circle-line text-primary-700" title="Facebook" />
                    )}
                    {b.tiktok_url && (
                      <i className="ri-tiktok-line text-foreground-700" title="TikTok" />
                    )}
                  </div>
                )}
              </div>

              <div className="mt-4 flex items-center gap-2">
                <button
                  onClick={() => openEdit(b)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-1.5 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
                >
                  <i className="ri-edit-line" /> Editar
                </button>
                <button
                  onClick={() => toggleActive(b)}
                  className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium whitespace-nowrap transition cursor-pointer ${
                    b.active
                      ? 'border border-background-300 text-foreground-700 hover:bg-background-100'
                      : 'bg-accent-500 text-background-50 hover:bg-accent-600'
                  }`}
                >
                  <i className={b.active ? 'ri-pause-circle-line' : 'ri-play-circle-line'} />
                  {b.active ? 'Desactivar' : 'Activar'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Editar sucursal' : 'Crear sucursal'}
        footer={
          <>
            <button
              onClick={() => setModalOpen(false)}
              disabled={saving}
              className="rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="rounded-md bg-primary-500 hover:bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition disabled:opacity-60 cursor-pointer"
            >
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </>
        }
      >
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className={labelClass}>Nombre de sucursal *</label>
            <input
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="Ej. Sucursal Punta Cana"
              className={inputClass}
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Código</label>
              <input
                value={form.code}
                onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))}
                placeholder="Ej. PC"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Teléfono</label>
              <input
                value={form.phone}
                onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                placeholder="809-000-0000"
                className={inputClass}
              />
            </div>
          </div>
          <div>
            <label className={labelClass}>Dirección</label>
            <input
              value={form.address}
              onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
              placeholder="Calle, ciudad"
              className={inputClass}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>WhatsApp</label>
              <input
                value={form.whatsapp}
                onChange={(e) => setForm((p) => ({ ...p, whatsapp: e.target.value }))}
                placeholder="809-000-0000"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Logo (URL)</label>
              <input
                value={form.logo_url}
                onChange={(e) => setForm((p) => ({ ...p, logo_url: e.target.value }))}
                placeholder="https://..."
                className={inputClass}
              />
            </div>
          </div>
          <div className="rounded-md border border-background-200 p-3 space-y-3">
            <p className="text-sm font-medium text-foreground-800 flex items-center gap-2">
              <i className="ri-share-line text-primary-700" /> Redes sociales de la sucursal
              <span className="text-xs font-normal text-foreground-400">(solo visibles en el catálogo)</span>
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className={labelClass}>
                  <i className="ri-instagram-line mr-1 text-accent-600" /> Instagram
                </label>
                <input
                  value={form.instagram_url}
                  onChange={(e) => setForm((p) => ({ ...p, instagram_url: e.target.value }))}
                  placeholder="@usuario"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>
                  <i className="ri-facebook-circle-line mr-1 text-primary-700" /> Facebook
                </label>
                <input
                  value={form.facebook_url}
                  onChange={(e) => setForm((p) => ({ ...p, facebook_url: e.target.value }))}
                  placeholder="@pagina"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>
                  <i className="ri-tiktok-line mr-1" /> TikTok
                </label>
                <input
                  value={form.tiktok_url}
                  onChange={(e) => setForm((p) => ({ ...p, tiktok_url: e.target.value }))}
                  placeholder="@usuario"
                  className={inputClass}
                />
              </div>
            </div>
          </div>
          <label className="flex items-center justify-between gap-3 rounded-md border border-background-200 p-3 cursor-pointer">
            <span className="text-sm font-medium text-foreground-900">Sucursal activa</span>
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))}
              className="w-5 h-5 accent-primary-500 cursor-pointer"
            />
          </label>
          {error && (
            <div className="rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5 text-sm text-foreground-700">
              {error}
            </div>
          )}
        </form>
      </Modal>
    </div>
  );
}